import {
    InsightFundScreenerRequestFundTypeEnum,
    InsightFundScreenerRequestInverseEnum,
    InsightFundScreenerRequestLeverageEnum,
    InsightGetFundsForHoldingRequestLeverageEnum,
    InsightGetTopFundsRequest,
    InsightGetTopFundsRequestFundTypeEnum,
    InsightGetTopFundsRequestInverseEnum,
    InsightGetTopFundsRequestLeverageEnum,
    InsightOrdering,
    InsightOrderingDirectionEnum,
    InsightPaginationV2,
    InsightPaging,
} from '@orbis-systems/orbis-api-clients/insight';
import { differenceInDays } from 'date-fns';
import { cloneDeep } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { API_DATE_YEAR_FIRST } from '../constants/datePatterns';
import {
    EtfBreakdownDuration,
    GlobalGroupCode,
    IndustryType,
    IpoDataSortOption,
    IpoPerformanceRange,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { Apis, MiddlewareConfig, Quote } from '../types';
import {
    BondScreenerValues,
    EarningsWithExtraInfo,
    EtfBreakdownWithExtraInfo,
    EtfDetails,
    EtfInit,
    EtfInitCommonParams,
    FundScreenerValues,
    IpoWithExtraInfo,
    OptionsRadarBreakdown,
    SectorBreakDown,
    SectorRanking,
    StockScreenerPresets,
    StockScreenerValues,
} from '../types/Research.type';
import {
    formatDate,
    formatEtfBySector,
    formatEtfInitCommonParams,
    formatIpo,
    formatUpcomingEarnings,
    getSectorIndustryByIndustryGroupCode,
} from '../utils';
import { QuoteApi } from './QuoteApi';

@injectable()
export class ResearchApi extends CachedResponseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.QuoteApi) protected readonly quoteApi: QuoteApi,
    ) {
        super(apis, config, cache);
    }

    async getEtfDetails(symbol: string): Promise<EtfDetails> {
        const response = await this.apis.orbis.research.etfs.details({ symbol });

        return response.data as EtfDetails;
    }

    async getSectorsRanking(industryType: IndustryType): Promise<SectorRanking[]> {
        const response = await this.apis.orbis.research.industryHeatMap({ industryType });

        return response.data as SectorRanking[];
    }

    async getSectorBreakdown(): Promise<SectorBreakDown> {
        const response = await this.apis.orbis.research.etfs.breakdownBySector({
            duration: EtfBreakdownDuration.OneDay,
            displaySize: 10,
            exposure: 70,
        });
        return response.data as SectorBreakDown;
    }

    async getTopTenEtfsBySector(industryGroupCode: GlobalGroupCode): Promise<EtfBreakdownWithExtraInfo[]> {
        const data = await this.getSectorBreakdown();
        return formatEtfBySector(data, industryGroupCode);
    }

    async getTopTenCompanies(industryGroupCode: GlobalGroupCode): Promise<Quote[]> {
        const response = await this.apis.orbis.research.symbolHeatMap({
            industryCode: industryGroupCode,
            top: true,
            numSymbols: 10,
            amountLimit: 1,
            exchangeFilter: 1,
        });
        return response.data as Quote[];
    }

    async getSectorIndustries(industryGroupCode: GlobalGroupCode): Promise<SectorRanking[]> {
        const data = await this.getSectorsRanking(IndustryType.Industry);
        return getSectorIndustryByIndustryGroupCode(data, industryGroupCode);
    }

    async getUpcomingEarnings({
        paging: { limit = 50, offset = 0 },
        symbols,
        days,
    }: {
        paging: { limit: number; offset: number };
        symbols?: string[];
        days?: number;
    }): Promise<EarningsWithExtraInfo[]> {
        const response = await this.apis.insight.earning.getEarningsCalendar({
            days_from_now: days || 1,
            load_quotes: true,
            symbols,
            paging: { limit, offset },
        });

        return response.data.details.map(formatUpcomingEarnings);
    }

    async getIpoUpcoming(paging: { limit: number; offset: number }): Promise<IpoWithExtraInfo[]> {
        const response = await this.apis.insight.ipos.getUpcoming(paging);
        return response.data.ipo_list.map(formatIpo);
    }

    async getIpoRecent(
        paging: {
            limit: number;
            offset: number;
        },
        order?: InsightOrdering,
        loadQuotes?: boolean,
        time: IpoPerformanceRange = IpoPerformanceRange.ThreeMonth,
    ): Promise<IpoWithExtraInfo[]> {
        const response = await this.apis.insight.ipos.postRecent({
            load_quotes: loadQuotes,
            order,
            paging,
            time,
        });

        return response.data.ipo_list.map(formatIpo);
    }

    async getIpoPerformance(
        paging: { limit: number; offset: number },
        loadQuotes?: boolean,
        time?: IpoPerformanceRange,
    ): Promise<IpoWithExtraInfo[]> {
        const [topIpoList, worstIpoList] = await Promise.all([
            this.getIpoRecent(
                paging,
                { direction: InsightOrderingDirectionEnum.Desc, field: IpoDataSortOption.IpoReturn },
                loadQuotes,
                time,
            ),
            this.getIpoRecent(
                paging,
                { direction: InsightOrderingDirectionEnum.Asc, field: IpoDataSortOption.IpoReturn },
                loadQuotes,
                time,
            ),
        ]);

        return [...topIpoList.filter((ipo) => ipo.ipo_return > 0), ...worstIpoList.filter((ipo) => ipo.ipo_return < 0)];
    }

    async getIpoDaysOnMarket(symbol: string): Promise<number> {
        const [performanceData, recentData] = await Promise.all([
            this.getIpoPerformance({ limit: 20, offset: 0 }, false, IpoPerformanceRange.OneYear),
            this.getIpoRecent({ limit: 20, offset: 0 }),
        ]);

        const ipo = [...performanceData, ...recentData].find((item) => item.symbol === symbol);
        const ipoDaysOnMarket = ipo?.ipo_date ? differenceInDays(new Date(), new Date(ipo.ipo_date)) : 0;

        return ipoDaysOnMarket;
    }

    async getEtfInitCommonParams(): Promise<EtfInitCommonParams> {
        const response = await this.apis.orbis.research.etfs.initScreener();
        return formatEtfInitCommonParams(response.data as EtfInit);
    }

    async getOptionRadar(): Promise<OptionsRadarBreakdown> {
        const response = await this.apis.orbis.research.optionRadar();

        return response.data as OptionsRadarBreakdown;
    }

    private async updateQuotePrice<
        T extends { symbol: string; last_price: number; price_change: number; price_change_percent?: number },
    >(data: T[]): Promise<T[]> {
        //FIXME: adding Daniils quote until insights will fix their quote data
        const symbols = data.map((item) => item.symbol);
        const quotes = await this.quoteApi.getQuotes(symbols);

        data.forEach((item) => {
            const currentQuote = quotes.find((quote) => quote.symbol === item.symbol);

            return {
                ...item,
                last_price: currentQuote?.lastPrice || item.last_price,
                price_change: currentQuote?.change || item.price_change,
                price_change_percent: currentQuote?.changePercent || item.price_change_percent || 0,
            };
        });

        return data;
    }

    async getEtfsForHolding(symbol: string, leverage = InsightGetFundsForHoldingRequestLeverageEnum.ANY) {
        const { data: etfsForHolding } = await this.apis.insight.fund.getFundsForHolding({
            symbol,
            leverage,
            paging: {
                limit: 20,
                offset: 0,
            },
            order: {
                direction: InsightOrderingDirectionEnum.Desc,
                field: 'holding_percent',
            },
        });

        await this.updateQuotePrice(etfsForHolding.data);

        return etfsForHolding;
    }

    async getStockScreener(preset: Partial<StockScreenerValues>, pagination: InsightPaginationV2) {
        const params = cloneDeep({
            ...preset,
            pagination,
        });

        if (preset.dividend_ex_date?.after) {
            // @ts-expect-error: backend requires a string
            params.dividend_ex_date.after = formatDate(preset.dividend_ex_date.after, { pattern: API_DATE_YEAR_FIRST });
        }

        if (preset.dividend_ex_date?.before) {
            // @ts-expect-error: backend requires a string
            params.dividend_ex_date.before = formatDate(preset.dividend_ex_date.before, {
                pattern: API_DATE_YEAR_FIRST,
            });
        }

        if (preset.dividend_pay_date?.after) {
            // @ts-expect-error: backend requires a string
            params.dividend_pay_date.after = formatDate(preset.dividend_pay_date.after, {
                pattern: API_DATE_YEAR_FIRST,
            });
        }

        if (preset.dividend_pay_date?.before) {
            // @ts-expect-error: backend requires a string
            params.dividend_pay_date.before = formatDate(preset.dividend_pay_date.before, {
                pattern: API_DATE_YEAR_FIRST,
            });
        }
        // @ts-expect-error: backend requires a string
        const { data: stockScreener } = await this.apis.insight.v2.stockScreener.stockScreener(params);

        await this.updateQuotePrice(stockScreener.data);

        return stockScreener;
    }

    async getStockScreenerPresets(limit: number, offset: number): Promise<StockScreenerPresets> {
        const params = { limit: limit || 10, offset: offset || 0 };
        const response = await this.apis.insight.stockScreener.getScreenerPresets(params);

        return response.data;
    }

    async getBondScreenerPresets(limit: number, offset: number) {
        const params = { limit: limit || 10, offset: offset || 0 };
        const response = await this.apis.insight.fixedIncome.getFixedIncomePresets(params);
        return response.data;
    }

    async getBondScreener(preset: Partial<BondScreenerValues>, pagination: InsightPaginationV2) {
        const params = cloneDeep({
            ...preset,
            pagination,
        });

        if (preset.maturity?.after) {
            // @ts-expect-error: backend requires a string
            params.maturity.after = formatDate(preset.maturity.after, { pattern: API_DATE_YEAR_FIRST });
        }

        if (preset.maturity?.before) {
            // @ts-expect-error: backend requires a string
            params.maturity.before = formatDate(preset.maturity.before, { pattern: API_DATE_YEAR_FIRST });
        }

        // @ts-expect-error: backend requires a string
        const { data: bondScreener } = await this.apis.insight.v2.fixedIncome.getFixedIncomeEntries(params);

        return bondScreener;
    }

    async getFundScreener(values: Partial<FundScreenerValues>, paging: InsightPaging) {
        const { data: fundScreener } = await this.apis.insight.fund.fundScreener({
            fund_type: InsightFundScreenerRequestFundTypeEnum.ANY,
            inverse: InsightFundScreenerRequestInverseEnum.ANY,
            leverage: InsightFundScreenerRequestLeverageEnum.ANY,
            ...values,
            paging,
        });

        await this.updateQuotePrice(fundScreener.details);

        return fundScreener;
    }

    async getFundScreenerFilters() {
        const response = await this.apis.insight.fund.getScreenerFilters();
        return response.data;
    }

    async getTopFunds({
        fund_type = InsightGetTopFundsRequestFundTypeEnum.ANY,
        inverse = InsightGetTopFundsRequestInverseEnum.ANY,
        leverage = InsightGetTopFundsRequestLeverageEnum.ANY,
        paging = { limit: 10, offset: 0 },
        order,
    }: Partial<InsightGetTopFundsRequest>) {
        const { data: topFund } = await this.apis.insight.fund.getTopFunds({
            fund_type,
            inverse,
            leverage,
            paging,
            order,
        });

        await this.updateQuotePrice(topFund.details);
        return topFund;
    }
}
